/**
 * One base hex per stage, keyed by stage name (shared by both pipelines). Two families:
 *  - "solid": legacy pills, base as background, white text.
 *  - "tinted": base @20% background, base @30% border, a dark (700-shade) text colour.
 * Unknown stages fall back to a neutral tinted grey. Colour is never the only signal — the label is always rendered.
 */
export type StageStyle = {
  family: 'solid' | 'tinted'; base: string; bg: string; border: string; text: string;
  /** Colour of the lead card's 3px edge. Equals base, except ללא מענה, whose edge is red-500 while its pill is the darker destructive red. */
  stripe: string;
  /** Lead cards of closed-lost stages are drawn at 70% opacity. */
  dimmed: boolean;
};

const STRIPE_OVERRIDE: Record<string, string> = { 'ללא מענה': '#EF4444' };
const DIMMED = new Set(['לא רלוונטי']);

const SOLID: Record<string, string> = {
  'חדש': '#18A5DC',
  'בטיפול': '#F59F0A',
  'נסגר': '#2BAB81',
  'לא רלוונטי': '#6E7D91',
  'ללא מענה': '#DC2828',
};

const TINTED: Record<string, { base: string; text: string }> = {
  'צריך בדיקת מימון': { base: '#06B6D4', text: '#0E7490' },
  'נקבעה פגישה עתידית': { base: '#8B5CF6', text: '#6D28D9' },
  'מימון גיל צעיר': { base: '#D946EF', text: '#A21CAF' },
  'לחזור מאוחר יותר': { base: '#22C55E', text: '#15803D' },
  'לעשות פולואפ': { base: '#F59E0B', text: '#B45309' },
  'מתעניין במוסטנג': { base: '#6366F1', text: '#4338CA' },
  'מעוניין למכור את הרכב': { base: '#EC4899', text: '#BE185D' },
  'למצוא רכב ספציפי': { base: '#84CC16', text: '#4D7C0F' },
  'ליד חם!!!': { base: '#EF4444', text: '#B91C1C' },
  'לא ברור': { base: '#22C55E', text: '#15803D' },
  'פגישה': { base: '#0EA5E9', text: '#0369A1' },
};

const FALLBACK = { base: '#64748B', text: '#334155' };

function alpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export function stageStyle(stageName: string): StageStyle {
  const name = stageName.trim();
  const solid = SOLID[name];
  const dimmed = DIMMED.has(name);
  if (solid) return { family: 'solid', base: solid, bg: solid, border: solid, text: '#FFFFFF', stripe: STRIPE_OVERRIDE[name] ?? solid, dimmed };
  const t = TINTED[name] ?? FALLBACK;
  return { family: 'tinted', base: t.base, bg: alpha(t.base, 0.2), border: alpha(t.base, 0.3), text: t.text, stripe: STRIPE_OVERRIDE[name] ?? t.base, dimmed };
}

export const KNOWN_STAGE_NAMES: string[] = [...Object.keys(SOLID), ...Object.keys(TINTED)];
